import { Component, OnInit, Output, EventEmitter, OnDestroy, Input } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { Subscription, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { PersonType } from 'src/app/services/models/enums';
import { MatButtonModule } from '@angular/material/button';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';
import { DeleteDocumentDialogComponent } from 'src/app/components/data-displayer/document-table/delete-document-dialog/delete-document-dialog.component';
import { HttpStatus } from 'src/app/shared/enums/http-status.enum';
import { TrackingRecordRegisterDialogComponent } from 'src/app/components/entity-manager/tracking-record-register-dialog.ts/tracking-record-register-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import {
  ConfirmDialogResultType,
  ConfirmDialogResult,
} from '../../../shared/components/modals/confirmation-dialog/confirmation-dialog.component';
import { GenericTableComponent } from 'src/app/shared/components/generic-table';
import { TableCellDirective } from 'src/app/shared/components/generic-table/directives/table-cell.directive';
import {
  getRegistryFavoriteTableConfig,
  REGISTRY_FAVORITE_NO_DATA_PERSON_MESSAGE,
  REGISTRY_FAVORITE_NO_DATA_COMPANY_MESSAGE,
} from './registry-favorite-table-config';
import { RegistryFavoriteFacade, RegistryFavoriteView } from './services/registry-favorite.facade';
import { CpfMaskPipe } from 'src/app/shared/pipes/cpf-mask.pipe';
import { CnpjMaskPipe } from 'src/app/shared/pipes/cnpj-mask.pipe';

@Component({
  selector: 'app-registry-favorite-list',
  templateUrl: './registry-favorite-list.component.html',
  styleUrls: ['./registry-favorite-list.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    GenericTableComponent,
    TableCellDirective,
    CpfMaskPipe,
    CnpjMaskPipe,
  ],
})
export class RegistryFavoriteListComponent implements OnInit, OnDestroy {
  @Input() showAdminActions: boolean = false;
  @Output()
  modelSendToRedirectIndividual = new EventEmitter();

  totalRegisters: number = 0;
  perPage: number = 10;
  page: number = 0;
  isLoading: boolean = false;

  favorites: RegistryFavoriteView[] = [];
  personType = PersonType.COMPANY;
  modelToSend = [];

  tableConfig = getRegistryFavoriteTableConfig();
  noDataMessage: string = REGISTRY_FAVORITE_NO_DATA_PERSON_MESSAGE;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly dialog: MatDialog,
    private readonly _snackBar: MatSnackBar,
    private readonly registryFavoriteFacade: RegistryFavoriteFacade,
    private readonly globalService: GlobalServiceService,
    private readonly localStorageService: LocalStorageService,
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.globalService.getPersonType$().subscribe((personType) => {
        this.personType = personType;
        this.updateNoDataMessage();
        this.loadMoreData();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get urlAssets() {
    const url = this.localStorageService.getItem('urlAssets');
    return url || '';
  }

  handleDelete(favorite: RegistryFavoriteView) {
    this.dialog
      .open(DeleteDocumentDialogComponent, {
        minWidth: '300px',
        data: favorite,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((close: ConfirmDialogResultType) => {
        if (close === ConfirmDialogResult.Confirm) {
          this.registryFavoriteFacade.deleteRegistry(favorite.id).subscribe({
            next: (response) => {
              if (response?.code === HttpStatus.OK && !response?.successful) {
                this.openSnackBarError(`${response.message}`);
              } else {
                this.totalRegisters--;
                this.openSnackBar($localize`Se ha dado de baja a `);
                this.loadMoreData();
              }
            },
            error: ({ error }) => {
              this.openSnackBarError(error.message);
            },
          });
        }
      });
  }

  handleEdit(favorite: RegistryFavoriteView) {
    this.registryFavoriteFacade.findByIdForEdit(favorite.id).subscribe({
      next: (dialogData) => {
        this.dialog.open(TrackingRecordRegisterDialogComponent, {
          data: dialogData,
          disableClose: true,
        });
      },
    });
  }

  toggleFavorite(favorite: RegistryFavoriteView) {
    const suscribeFavoriteRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: $localize`:@@favoriteRemovalTitle:Eliminación de favorito`,
        message: $localize`:@@confirmDocumentFavoriteRemoval:¿Confirmar la eliminación de lo documento "${favorite.document}" de la lista de favoritos?`,
        confirmButton: {
          text: $localize`:@@js_Remove:Eliminar`,
          style: 'danger',
          icon: 'delete_outlined',
        },
      },
    });

    suscribeFavoriteRef.afterClosed().subscribe((close: ConfirmDialogResultType) => {
      if (close === ConfirmDialogResult.Confirm) {
        this.registryFavoriteFacade.toggleFavorite(favorite.id).subscribe({
          next: () => {
            this.loadMoreData();
          },
        });
      }
    });
  }

  getFavoriteIcon() {
    let iconPath = `${this.urlAssets}/icons/`;

    iconPath +=
      this.personType === PersonType.COMPANY ? 'company-select.png' : 'persona-select.png';

    return iconPath;
  }

  changePage(page = 0) {
    this.page = page;
    this.loadMoreData();
  }

  updateNoDataMessage() {
    this.noDataMessage =
      this.personType === PersonType.PERSON
        ? REGISTRY_FAVORITE_NO_DATA_PERSON_MESSAGE
        : REGISTRY_FAVORITE_NO_DATA_COMPANY_MESSAGE;
  }

  loadMoreData() {
    this.isLoading = true;
    this.registryFavoriteFacade.listFavorites(this.page, this.perPage).subscribe({
      next: (response) => {
        this.favorites = response.items;
        this.totalRegisters = response.total;
        this.isLoading = false;
      },
      error: () => {
        this.favorites = [];
        this.totalRegisters = 0;
        this.isLoading = false;
      },
    });
  }

  openSnackBar(message: string) {
    this._snackBar.open(message, $localize`:@@js_Close:Cerrar`, {
      duration: 7000,
    });
  }

  openSnackBarError(message: string) {
    this._snackBar.open(message, $localize`:@@js_Close:Cerrar`, {
      duration: 7000,
      panelClass: ['snackbar-fail'],
    });
  }
}
